import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  updateProfile,
  updatePassword,
  verifyBeforeUpdateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const BRAND_ASSET_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-generate-brand-asset-url';
const MAX_DIMENSION = 256;
const MAX_BYTES = 80 * 1024; // 80KB

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > height) {
        if (width > MAX_DIMENSION) {
          height = Math.round(height * (MAX_DIMENSION / width));
          width = MAX_DIMENSION;
        }
      } else {
        if (height > MAX_DIMENSION) {
          width = Math.round(width * (MAX_DIMENSION / height));
          height = MAX_DIMENSION;
        }
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      // Try decreasing quality until under MAX_BYTES
      let quality = 0.9;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Canvas compression failed'));
            if (blob.size <= MAX_BYTES || quality <= 0.3) {
              resolve(blob);
            } else {
              quality -= 0.1;
              tryCompress();
            }
          },
          'image/jpeg',
          quality
        );
      };
      tryCompress();
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const GIDU_LIST_URL   = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/gidu-list-seats';
const GIDU_INVITE_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/gidu-invite';

// Products a seat can be granted. Scope "all" is steward-only (D-024); the form
// enforces that by only offering it when the invited level is steward.
const GIDU_PRODUCTS = ['smartdata', 'smartshop', 'smartcom', 'smartfinance'];

async function uploadBrandAsset(assetType, blob) {
  const idToken = await auth.currentUser.getIdToken();
  const response = await fetch(BRAND_ASSET_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id_token: idToken,
      asset_type: assetType,
      mime_type: 'image/jpeg',
    }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to get upload URL');

  await fetch(data.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg' },
    body: blob,
  });

  return `${data.public_url}?v=${Date.now()}`;
}

function SettingsPanel({ gido, onClose, onPreviewGlow, onRefresh }) {
  const [activeTab, setActiveTab] = useState('brand');
  const [primary, setPrimary] = useState('#D4AF37');
  const [glowMode, setGlowMode] = useState('off');
  const [glowColor, setGlowColor] = useState('primary');
  const [company, setCompany] = useState({
    legal_name: '', trading_name: '', slogan: '', vat_number: '',
    street: '', city: '', postcode: '', country: '',
    hours_opening: '', hours_service: '',
    phone_landline: '', phone_mobile: '', whatsapp: '', email_public: '',
  });
  const [companySaving, setCompanySaving] = useState(false);
  const [companyStatus, setCompanyStatus] = useState(null);

  const [secondary, setSecondary] = useState('#1a1a1a');
  const [accent, setAccent] = useState('');
  useEffect(() => {
    if (!onPreviewGlow) return;
    if (glowMode === 'off') { onPreviewGlow(null); return; }
    onPreviewGlow({ mode: glowMode, single_color: glowColor, colors: { primary, secondary, accent: accent || null } });
  }, [glowMode, glowColor, primary, secondary, accent, onPreviewGlow]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [avatarError, setAvatarError] = useState(null);
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [personalSaving, setPersonalSaving] = useState(false);
  const [personalStatus, setPersonalStatus] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [emailSaving, setEmailSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [team, setTeam] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLevel, setInviteLevel] = useState('operator');
  const [inviteGrants, setInviteGrants] = useState({});
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);

  useEffect(() => {
    async function loadBrand() {
      if (!gido?.gido_id) {
        setLoading(false);
        return;
      }
      try {
        const brandDoc = await getDoc(doc(db, 'smart_brand', gido.gido_id));
        if (brandDoc.exists()) {
          const data = brandDoc.data();
          if (data.visual?.colors?.primary) setPrimary(data.visual.colors.primary);
          if (data.visual?.colors?.secondary) setSecondary(data.visual.colors.secondary);
          if (data.visual?.colors?.accent) setAccent(data.visual.colors.accent);
          if (data.visual?.glow_mode) setGlowMode(data.visual.glow_mode);
          if (data.visual?.glow_color) setGlowColor(data.visual.glow_color);
          if (data.company) setCompany((prev) => ({ ...prev, ...data.company }));
          if (data.visual?.logo_url) setLogoPreview(data.visual.logo_url);
          if (data.visual?.avatar_url) setAvatarPreview(data.visual.avatar_url);
        }
      } catch (err) {
        console.error('Failed to load smart_brand:', err);
      }
      if (auth.currentUser) {
        setFullName(auth.currentUser.displayName || '');
        setCurrentEmail(auth.currentUser.email || '');
        const regDoc = await getDoc(doc(db, 'gido_registry', auth.currentUser.uid));
        if (regDoc.exists()) {
          setCompanyName(regDoc.data().company_name || '');
          setPhone(regDoc.data().phone || '');
        }
      }
      setLoading(false);
    }
    loadBrand();
  }, [gido]);

  async function handleCompanySave() {
    if (!gido?.gido_id) return;
    setCompanySaving(true);
    setCompanyStatus(null);
    try {
      await setDoc(
        doc(db, 'smart_brand', gido.gido_id),
        { company, updated_at: new Date().toISOString() },
        { merge: true }
      );
      setCompanyStatus('success');
    } catch (err) {
      console.error('Company save failed:', err);
      setCompanyStatus('error');
    }
    setCompanySaving(false);
  }

  const setField = (k, v) => setCompany((prev) => ({ ...prev, [k]: v }));

  async function handleApply() {
    if (!gido?.gido_id) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      await setDoc(
        doc(db, 'smart_brand', gido.gido_id),
        {
          visual: {
            colors: { primary, secondary, accent: accent || null },
            glow_mode: glowMode,
            glow_color: glowColor,
          },
          updated_at: new Date().toISOString(),
        },
        { merge: true }
      );
      setSaveStatus('success');
    } catch (err) {
      console.error('Failed to save smart_brand:', err);
      setSaveStatus('error');
    }
    setSaving(false);
  }

  async function handleImageSelect(e, assetType) {
    const file = e.target.files[0];
    if (!file) return;
    const setUploading = assetType === 'logo' ? setUploadingLogo : setUploadingAvatar;
    const setPreview = assetType === 'logo' ? setLogoPreview : setAvatarPreview;
    const setError = assetType === 'logo' ? setLogoError : setAvatarError;
    setError(null);
    setUploading(true);
    try {
      const blob = await resizeImage(file);
      const previewUrl = URL.createObjectURL(blob);
      setPreview(previewUrl);
      const publicUrl = await uploadBrandAsset(assetType, blob);
      await setDoc(
        doc(db, 'smart_brand', gido.gido_id),
        {
          visual: { [assetType === 'logo' ? 'logo_url' : 'avatar_url']: publicUrl },
          updated_at: new Date().toISOString(),
        },
        { merge: true }
      );
      setPreview(publicUrl);
    } catch (err) {
      console.error(`Failed to upload ${assetType}:`, err);
      setError(`Failed to upload ${assetType}. Please try again.`);
    }
    setUploading(false);
  }

  async function handlePersonalSave() {
    setPersonalSaving(true);
    setPersonalStatus(null);
    try {
      await updateProfile(auth.currentUser, { displayName: fullName });

      // D-082: a seat holder has NO gido_registry record. Writing here would not
      // edit the host organisation - it would mint a stray registry document
      // against their own uid. company_name is the owner's to change.
      const fields = { full_name: fullName, phone };
      if (gido?.is_owner !== false) fields.company_name = companyName;

      await setDoc(
        doc(db, 'gido_registry', auth.currentUser.uid),
        fields,
        { merge: true }
      );
      setPersonalStatus('success');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Personal save failed:', err);
      setPersonalStatus('error');
    }
    setPersonalSaving(false);
  }

  async function loadTeam() {
    setTeamLoading(true);
    setTeamError(null);
    try {
      const idToken = await auth.currentUser.getIdToken();
      // D-082: a uid no longer identifies one organisation. The org is named by the
      // client and re-verified server-side by resolve_gidu on every call - never
      // derived from the token alone, never cached and trusted.
      const res = await fetch(GIDU_LIST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ owner_gido: gido?.gido_id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTeamError(data.message || data.error || 'Could not load your team.');
        setTeam(null);
      } else {
        setTeam(data);
      }
    } catch (e) {
      setTeamError('Could not reach the server.');
      setTeam(null);
    }
    setTeamLoading(false);
  }

  // An operator is read-only and can never hold scope "all" (D-024). The form mirrors
  // the backend ceiling so a steward cannot build an invite the server would refuse.
  function toggleGrant(product, access) {
    setInviteGrants((prev) => {
      const next = { ...prev };
      if (!access) {
        delete next[product];
      } else {
        next[product] = { access, scope: 'own' };
      }
      return next;
    });
  }

  async function handleInvite() {
    setInviteSaving(true);
    setInviteStatus(null);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(GIDU_INVITE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({
          owner_gido: gido?.gido_id,
          email: inviteEmail.trim().toLowerCase(),
          license_level: inviteLevel,
          grants: inviteGrants,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setInviteStatus(data.message || data.error || 'Invitation failed.');
      } else {
        setInviteStatus('success');
        setInviteEmail('');
        setInviteGrants({});
        loadTeam();
      }
    } catch (e) {
      setInviteStatus('Could not reach the server.');
    }
    setInviteSaving(false);
  }

  async function reauth() {
    const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
  }

  async function handleEmailChange() {
    setEmailSaving(true);
    setEmailStatus(null);
    try {
      await reauth();
      // Reset 2FA first — the old authenticator entry is orphaned by the email change.
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('https://europe-west1-dbos-core-prod.cloudfunctions.net/twofa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'unenroll' }),
      });
      if (!res.ok) throw new Error('2FA reset failed');
      // Send the verify-before-update link to the new address.
      await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
      setEmailStatus('success');
      // Sign out so they confirm the new email + enroll fresh 2FA on next login.
      setTimeout(() => auth.signOut(), 5000);
    } catch (err) {
      console.error('Email change failed:', err);
      setEmailStatus(err.code === 'auth/wrong-password' ? 'Wrong password' : 'Failed to update email');
    }
    setEmailSaving(false);
  }

  async function handlePasswordChange() {
    if (newPassword !== confirmNewPassword) {
      setPasswordStatus('Passwords do not match');
      return;
    }
    setPasswordSaving(true);
    setPasswordStatus(null);
    try {
      await reauth();
      await updatePassword(auth.currentUser, newPassword);
      setPasswordStatus('success');
      setNewPassword('');
      setConfirmNewPassword('');
      setCurrentPassword('');
    } catch (err) {
      console.error('Password change failed:', err);
      setPasswordStatus(err.code === 'auth/wrong-password' ? 'Wrong password' : 'Failed to update password');
    }
    setPasswordSaving(false);
  }

  return (
    <div className="settings-overlay">
      <div className="settings-modal">
        <div className="settings-header">
          <div className="settings-title-block">
            <h2>SmartBrand Settings</h2>
            <p className="settings-tagline">Set it once. It flows everywhere.</p>
            <p className="settings-subtagline">Your logo, colours and details apply automatically across SmartMail, SmartWeb, SmartFinance and every DBOS solution.</p>
          </div>
          <button className="settings-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="settings-tabs">
          <button
            className={activeTab === 'brand' ? 'settings-tab active' : 'settings-tab'}
            onClick={() => setActiveTab('brand')}
          >
            Brand
          </button>
          <button
            className={activeTab === 'account' ? 'settings-tab active' : 'settings-tab'}
            onClick={() => setActiveTab('account')}
          >
            Account
          </button>
          <button
            className={activeTab === 'company' ? 'settings-tab active' : 'settings-tab'}
            onClick={() => setActiveTab('company')}
          >
            Company
          </button>
          <button
            className={activeTab === 'team' ? 'settings-tab active' : 'settings-tab'}
            onClick={() => { setActiveTab('team'); if (!team) loadTeam(); }}
          >
            People
          </button>
        </div>

        {activeTab === 'brand' && (
          <div className="settings-tab-content">
            {loading ? (
              <p className="settings-hint">Loading...</p>
            ) : (
              <>
                <p className="settings-hint">
                  Changes preview here only. Nothing applies until you click Apply.
                </p>

                <label className="settings-field-label">Company logo</label>
                <div className="settings-image-upload">
                  {logoPreview && <img src={logoPreview} alt="Logo preview" className="settings-image-preview" />}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, 'logo')}
                    disabled={uploadingLogo}
                  />
                  {uploadingLogo && <span className="settings-upload-status">Uploading...</span>}
                </div>
                {logoError && <p className="settings-save-error">{logoError}</p>}

                <label className="settings-field-label">Avatar</label>
                <div className="settings-image-upload">
                  {avatarPreview && <img src={avatarPreview} alt="Avatar preview" className="settings-image-preview settings-image-round" />}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, 'avatar')}
                    disabled={uploadingAvatar}
                  />
                  {uploadingAvatar && <span className="settings-upload-status">Uploading...</span>}
                </div>
                {avatarError && <p className="settings-save-error">{avatarError}</p>}

                <label className="settings-field-label">Primary colour</label>
                <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} />

                <label className="settings-field-label">Secondary colour</label>
                <input type="color" value={secondary} onChange={(e) => setSecondary(e.target.value)} />

                <label className="settings-field-label">Accent colour (optional)</label>
                <input
                  type="color"
                  value={accent || '#000000'}
                  onChange={(e) => setAccent(e.target.value)}
                />

                <label className="settings-field-label">Ambient glow frame</label>
                <div className="glow-mode-select">
                  {['off', 'single', 'multi'].map((m) => (
                    <button
                      key={m}
                      className={glowMode === m ? 'glow-mode-btn active' : 'glow-mode-btn'}
                      onClick={() => setGlowMode(m)}
                    >
                      {m === 'off' ? 'Off' : m === 'single' ? 'Single' : 'Multi'}
                    </button>
                  ))}
                </div>

                {glowMode === 'single' && (
                  <div className="glow-color-select">
                    <span className="glow-color-label">Glow colour:</span>
                    {[['primary', primary], ['secondary', secondary], ['accent', accent || '#000000']].map(([key, col]) => (
                      <button
                        key={key}
                        className={glowColor === key ? 'glow-color-dot active' : 'glow-color-dot'}
                        style={{ background: col }}
                        onClick={() => setGlowColor(key)}
                        title={key}
                      />
                    ))}
                  </div>
                )}

                <div className="settings-actions">
                  <button className="settings-apply-btn" onClick={handleApply} disabled={saving}>
                    {saving ? 'Saving...' : 'Apply'}
                  </button>
                  {saveStatus === 'success' && <span className="settings-save-success">✓ Saved</span>}
                  {saveStatus === 'error' && <span className="settings-save-error">✗ Save failed</span>}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'account' && (
          <div className="settings-tab-content">
            <label className="settings-field-label">Full name</label>
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />

            <label className="settings-field-label">Current email</label>
            <input type="email" value={currentEmail} disabled />

            <label className="settings-field-label">Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                const v = e.target.value;
                if (/^[0-9+() ]*$/.test(v)) setPhone(v);
              }}
            />

            {gido?.is_owner !== false ? (
              <>
                <label className="settings-field-label">Registered name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </>
            ) : (
              <p className="settings-field-note">
                The registered name of {gido?.company_name || 'this organisation'} can
                only be changed by its owner.
              </p>
            )}

            <div className="settings-actions">
              <button className="settings-apply-btn" onClick={handlePersonalSave} disabled={personalSaving}>
                {personalSaving ? 'Saving...' : 'Save'}
              </button>
              {personalStatus === 'success' && <span className="settings-save-success">✓ Saved</span>}
              {personalStatus === 'error' && <span className="settings-save-error">✗ Failed</span>}
            </div>

            <hr style={{border: 'none', borderTop: '1px solid var(--dark-border)', margin: '24px 0'}} />

            <label className="settings-field-label">Current password (required for changes below)</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />

            <label className="settings-field-label">New email</label>
            <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            <div className="settings-actions">
              <button className="settings-apply-btn" onClick={handleEmailChange} disabled={emailSaving || !currentPassword || !newEmail}>
                {emailSaving ? 'Saving...' : 'Change email'}
              </button>
              {emailStatus === 'success' && <span className="settings-save-success">✓ Check your new inbox to confirm. You'll set up 2FA again on next login — delete the old "DBOS" entry in your authenticator.</span>}
              {emailStatus && emailStatus !== 'success' && <span className="settings-save-error">{emailStatus}</span>}
            </div>

            <label className="settings-field-label">New password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <label className="settings-field-label">Confirm new password</label>
            <input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} />
            <div className="settings-actions">
              <button className="settings-apply-btn" onClick={handlePasswordChange} disabled={passwordSaving || !currentPassword || !newPassword}>
                {passwordSaving ? 'Saving...' : 'Change password'}
              </button>
              {passwordStatus === 'success' && <span className="settings-save-success">✓ Saved</span>}
              {passwordStatus && passwordStatus !== 'success' && <span className="settings-save-error">{passwordStatus}</span>}
            </div>
          </div>
        )}

        {activeTab === 'company' && (
          <div className="settings-tab-content">
            <p className="settings-hint">Used by SmartWeb, SmartMail & SmartFinance to auto-fill your sites, emails and invoices.</p>

            <label className="settings-field-label">Legal name</label>
            <input type="text" value={company.legal_name} onChange={(e) => setField('legal_name', e.target.value)} />

            <label className="settings-field-label">Trading name</label>
            <input type="text" value={company.trading_name} onChange={(e) => setField('trading_name', e.target.value)} />
            <button className="same-as-btn" onClick={() => setField('trading_name', gido?.company_name || '')}>Same as registered name</button>

            <label className="settings-field-label">Slogan / Claim</label>
            <input type="text" value={company.slogan} onChange={(e) => setField('slogan', e.target.value)} />

            <label className="settings-field-label">VAT number</label>
            <input type="text" value={company.vat_number} onChange={(e) => setField('vat_number', e.target.value)} />

            <label className="settings-field-label">Street</label>
            <input type="text" value={company.street} onChange={(e) => setField('street', e.target.value)} />
            <label className="settings-field-label">City</label>
            <input type="text" value={company.city} onChange={(e) => setField('city', e.target.value)} />
            <label className="settings-field-label">Postcode</label>
            <input type="text" value={company.postcode} onChange={(e) => setField('postcode', e.target.value)} />
            <label className="settings-field-label">Country</label>
            <input type="text" value={company.country} onChange={(e) => setField('country', e.target.value)} />

            <label className="settings-field-label">Opening hours</label>
            <input type="text" value={company.hours_opening} onChange={(e) => setField('hours_opening', e.target.value)} placeholder="Mon-Fri 9-17" />
            <label className="settings-field-label">Service hours</label>
            <input type="text" value={company.hours_service} onChange={(e) => setField('hours_service', e.target.value)} />
            <button className="same-as-btn" onClick={() => setField('hours_service', company.hours_opening)}>Same as opening hours</button>

            <label className="settings-field-label">Landline</label>
            <input type="tel" value={company.phone_landline} onChange={(e) => setField('phone_landline', e.target.value)} />
            <label className="settings-field-label">Mobile</label>
            <input type="tel" value={company.phone_mobile} onChange={(e) => setField('phone_mobile', e.target.value)} />
            <button className="same-as-btn" onClick={() => setField('phone_mobile', phone)}>Same as personal phone</button>
            <label className="settings-field-label">WhatsApp</label>
            <input type="tel" value={company.whatsapp} onChange={(e) => setField('whatsapp', e.target.value)} />
            <button className="same-as-btn" onClick={() => setField('whatsapp', company.phone_mobile)}>Same as mobile</button>

            <label className="settings-field-label">Public email</label>
            <input type="email" value={company.email_public} onChange={(e) => setField('email_public', e.target.value)} />

            <div className="settings-actions">
              <button className="settings-apply-btn" onClick={handleCompanySave} disabled={companySaving}>
                {companySaving ? 'Saving...' : 'Save'}
              </button>
              {companyStatus === 'success' && <span className="settings-save-success">✓ Saved</span>}
              {companyStatus === 'error' && <span className="settings-save-error">✗ Failed</span>}
            </div>
          </div>
        )}
        {activeTab === 'team' && (
          <div className="settings-tab-content">
            <p className="settings-hint">
              Everyone who can work inside your DBOS environment. Each person signs in with
              their own login and their own second factor - you never see their password.
            </p>

            {teamLoading && <p className="settings-hint">Loading...</p>}
            {teamError && <p className="settings-save-error">{teamError}</p>}

            {team && (
              <>
                <label className="settings-field-label">Your organisation</label>
                <div className="settings-hint" style={{marginBottom: '16px'}}>
                  {team.owner
                    ? <><strong>{team.owner.name}</strong> ({team.owner.email}) - steward</>
                    : <span className="settings-muted">Owner record not available</span>}
                </div>

                <label className="settings-field-label">People with a seat</label>
                {team.seats.length === 0 && (
                  <p className="settings-hint">Nobody yet. Invite someone below.</p>
                )}
                {team.seats.map((s) => (
                  <div key={s.uid} className="settings-hint" style={{marginBottom: '8px'}}>
                    <strong>{s.name || s.email || s.uid}</strong>
                    {s.email && s.name ? ` (${s.email})` : ''} - {s.license_level}
                    {Object.keys(s.grants).length > 0 &&
                      ` - ${Object.keys(s.grants).join(', ')}`}
                  </div>
                ))}

                {team.invites.length > 0 && (
                  <>
                    <label className="settings-field-label">Invited, not yet accepted</label>
                    {team.invites.map((i) => (
                      <div key={i.token_id} className="settings-hint" style={{marginBottom: '8px'}}>
                        {i.email} - {i.license_level} - expires {new Date(i.expires_at).toLocaleDateString()}
                      </div>
                    ))}
                  </>
                )}

                {team.consultants.length > 0 && (
                  <>
                    <label className="settings-field-label">DBOS support access</label>
                    <p className="settings-hint" style={{fontSize: '12px'}}>
                      Time-limited access you granted to DBOS staff. It ends automatically
                      on the date shown.
                    </p>
                    {team.consultants.map((c) => (
                      <div key={c.dbos_uid} className="settings-hint" style={{marginBottom: '8px'}}>
                        {c.role_hint || 'DBOS'} - {c.license_level} - until{' '}
                        {new Date(c.expires_at).toLocaleDateString()}
                      </div>
                    ))}
                  </>
                )}

                <hr style={{border: 'none', borderTop: '1px solid var(--dark-border)', margin: '24px 0'}} />

                <label className="settings-field-label">Invite someone</label>
                <input
                  type="email"
                  placeholder="their email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />

                <label className="settings-field-label">Their level</label>
                <select value={inviteLevel} onChange={(e) => setInviteLevel(e.target.value)}>
                  <option value="operator">Operator - can view, cannot change</option>
                  <option value="builder">Builder - can view and change</option>
                  <option value="steward">Steward - full access, can invite others</option>
                </select>

                <label className="settings-field-label">What they can reach</label>
                {GIDU_PRODUCTS.map((p) => {
                  const current = inviteGrants[p];
                  return (
                    <div key={p} className="settings-hint" style={{marginBottom: '6px'}}>
                      <label style={{marginRight: '12px'}}>
                        <input
                          type="checkbox"
                          checked={!!current}
                          onChange={(e) => toggleGrant(p, e.target.checked ? 'read' : null)}
                        />{' '}
                        {p}
                      </label>
                      {current && inviteLevel !== 'operator' && (
                        <label>
                          <input
                            type="checkbox"
                            checked={current.access === 'write'}
                            onChange={(e) => toggleGrant(p, e.target.checked ? 'write' : 'read')}
                          />{' '}
                          can change
                        </label>
                      )}
                    </div>
                  );
                })}

                <div className="settings-actions">
                  <button
                    className="settings-apply-btn"
                    onClick={handleInvite}
                    disabled={inviteSaving || !inviteEmail || Object.keys(inviteGrants).length === 0}
                  >
                    {inviteSaving ? 'Sending...' : 'Send invitation'}
                  </button>
                  {inviteStatus === 'success' && (
                    <span className="settings-save-success">Invitation sent</span>
                  )}
                  {inviteStatus && inviteStatus !== 'success' && (
                    <span className="settings-save-error">{inviteStatus}</span>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SettingsPanel;
